import { useState } from "react";
import { Mail, X } from "lucide-react";

import { MAX_ESCALATE_AFTER_H } from "@/data/alarmRules/engineConstants";
import { cn } from "@/lib/cn";
import { NumberField } from "@/components/ui/NumberField";

interface Escalation {
  after_h: number;
  recipients: string[];
}

export interface CloudRuleNotification {
  instant: string[];
  escalation?: Escalation;
  notify_on_clear?: boolean;
}

interface Props {
  value: CloudRuleNotification | null;
  onChange: (next: CloudRuleNotification | null) => void;
}

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

/**
 * v4 — Recipients panel.
 *
 * Operators choose between three modes:
 *
 *   "off"   — no recipients; rule still evaluates and emits a TB alarm
 *             but carries no email side-effect.
 *   "on"    — at least one instant recipient on raise. Optional
 *             escalation list re-emails after N hours of continuous
 *             active state. Optional "send an email on clear" toggle.
 *
 * Cloud-authored rules carry INLINE email addresses only — EMS-Admin's
 * local recipient-group abstraction is not visible here. The edge
 * resolver handles both forms.
 */
export function RecipientsPanel({ value, onChange }: Props) {
  const enabled = value !== null;
  return (
    <div className="border border-edge rounded p-3 space-y-3 bg-surface-2/30">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-text-faint flex items-center gap-1.5">
          <Mail className="size-3" /> Recipients
        </div>
        <label className="flex items-center gap-2 text-[12px] cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? { instant: [], notify_on_clear: false }
                  : null,
              )
            }
          />
          <span>Email someone when this fires</span>
        </label>
      </div>
      {!enabled && (
        <p className="text-[11px] text-text-faint italic">
          The rule still evaluates and emits a ThingsBoard alarm, but
          carries no email recipients — no email side effect.
        </p>
      )}
      {enabled && value && (
        <NotificationFields value={value} onChange={onChange} />
      )}
    </div>
  );
}

function NotificationFields({
  value,
  onChange,
}: {
  value: CloudRuleNotification;
  onChange: (next: CloudRuleNotification) => void;
}) {
  const hasEscalation = !!value.escalation;
  const noInstant = value.instant.length === 0;
  const noEscalationRecipients = hasEscalation && value.escalation!.recipients.length === 0;
  return (
    <div className="space-y-3">
      <EmailChipList
        label="Instant recipients (on raise)"
        emails={value.instant}
        onChange={(emails) => onChange({ ...value, instant: emails })}
      />
      {noInstant && (
        <p className="text-[11px] text-critical/90">
          <strong>Add at least one email above</strong> — the rule's
          envelope won't validate at the edge with an empty instant
          list, so the rule would be silently dropped after save.
        </p>
      )}
      <label className="flex items-center gap-2 text-[12px] cursor-pointer">
        <input
          type="checkbox"
          checked={hasEscalation}
          onChange={(e) =>
            onChange({
              ...value,
              escalation: e.target.checked
                ? { after_h: 24, recipients: [] }
                : undefined,
            })
          }
        />
        <span>Escalate when still active after some hours</span>
      </label>
      {noEscalationRecipients && (
        <p className="text-[11px] text-critical/90 ml-6">
          <strong>Add at least one escalation email.</strong> An
          escalation block with empty recipients fails edge validation.
        </p>
      )}
      {hasEscalation && value.escalation && (
        <div className="ml-6 border-l-2 border-edge pl-3 space-y-2">
          <label className="flex items-center gap-2 text-[12px]">
            <span className="text-text-faint">after</span>
            <NumberField
              value={value.escalation.after_h}
              onChange={(v) =>
                onChange({
                  ...value,
                  escalation: { ...value.escalation!, after_h: v },
                })
              }
              min={0.01}
              max={MAX_ESCALATE_AFTER_H}
              step={0.5}
              className="form-input w-24 text-[12px]"
            />
            <span className="text-text-faint">
              hours of continuous active state, re-email:
            </span>
          </label>
          <EmailChipList
            label="Escalation recipients"
            emails={value.escalation.recipients}
            onChange={(emails) =>
              onChange({
                ...value,
                escalation: { ...value.escalation!, recipients: emails },
              })
            }
          />
        </div>
      )}
      <label className="flex items-start gap-2 text-[12px] cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={value.notify_on_clear ?? false}
          onChange={(e) =>
            onChange({ ...value, notify_on_clear: e.target.checked })
          }
        />
        <span>
          <strong>Send a "resolved" email when the alarm clears.</strong>
          <span className="text-text-faint">
            &nbsp;Default: off. When on, the same instant recipient list is
            emailed once the condition resolves.
          </span>
        </span>
      </label>
    </div>
  );
}

function EmailChipList({
  label,
  emails,
  onChange,
}: {
  label: string;
  emails: string[];
  onChange: (next: string[]) => void;
}) {
  const [pending, setPending] = useState("");
  const [error, setError] = useState<string | null>(null);

  function commit(raw: string) {
    const e = raw.trim();
    if (!e) return;
    if (!EMAIL_RE.test(e)) {
      setError(`Not a valid email: ${e}`);
      return;
    }
    if (emails.includes(e)) {
      setError("Already in the list.");
      return;
    }
    onChange([...emails, e]);
    setPending("");
    setError(null);
  }

  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-wider text-text-faint">
        {label}
      </div>
      <div className="flex flex-wrap gap-1">
        {emails.map((e, i) => (
          <span
            key={e}
            className="border border-edge bg-surface-2/60 rounded px-2 py-0.5 text-[11px] flex items-center gap-1 font-mono"
          >
            {e}
            <button
              type="button"
              onClick={() =>
                onChange(emails.filter((_, idx) => idx !== i))
              }
              className="text-text-faint hover:text-critical"
              aria-label={`Remove ${e}`}
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="email"
          placeholder="add email…"
          value={pending}
          onChange={(e) => {
            setPending(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit(pending);
            }
          }}
          onBlur={() => pending && commit(pending)}
          className={cn(
            "form-input text-[12px] flex-1",
            error && "border-critical/60",
          )}
        />
      </div>
      {error && (
        <div className="text-critical text-[11px]">{error}</div>
      )}
    </div>
  );
}
